import { Injectable, OnDestroy} from '@angular/core';
import { Location } from '@angular/common';
import { LangService } from '../core/lang.service';
import { Router } from '@angular/router';
import { StudentDashboardView } from './view-student-g9-dashboard/view-student-g9-dashboard.component';
import { AuthService } from '../api/auth.service';
import { RoutesService } from '../api/routes.service';
import { StudentG9ConnectionService } from './student-g9-connection.service';
import { OnlineOrPaperService } from '../ui-testrunner/online-or-paper.service';
import { G9DemoDataService } from '../ui-schooladmin/g9-demo-data.service';
import { LoginGuardService } from '../api/login-guard.service';
import { StyleprofileService } from '../core/styleprofile.service';
import { AssessmentLockService } from '../core/assessmentlock.service';
import { VerifiedAccessDebugService } from '../core/verified-access-debug.service';

@Injectable({
  providedIn: 'root'
})
export class StudentDashboardService implements OnDestroy {

  constructor(
    private lang: LangService,
    private router: Router,
    private auth: AuthService,
    private routes: RoutesService,
    private studentG9Connection: StudentG9ConnectionService,
    private onlineOrPaper: OnlineOrPaperService,
    private g9DemoData: G9DemoDataService,
    private loginGuard: LoginGuardService,
    private styleprofile: StyleprofileService,
    private location: Location,
    private assessmentLock: AssessmentLockService,
    private verifiedAccessDebug: VerifiedAccessDebugService
  ) {
  }

  userSub;
  private schoolSoftLockEnable: boolean = false
  private schoolSoftLockPjEnable: boolean = false
  private schoolSoftLockG9Enable: boolean = false
  private schoolSoftLockG10Enable: boolean = false
  private _enforceTdLang: boolean = false;
  private _tdLang: string;

  init() {
    if(!this.userSub) {
      this.userSub = this.auth.user().subscribe(async (info) => {
        await this.studentG9Connection.onUserInfoChange(info);
      });
    }
  }


  ngOnDestroy() {
    if(this.userSub) {
      this.userSub.unsubscribe();
    }
  }

  logAction(logActionSlug: string, body: {session_id?: string, state?: object, info?: object}, slugPrefix? : string): void {
    if(!this.auth.userIsStudent()) {
      return;
    }
    let slug = logActionSlug;
    if (slugPrefix !== null && slugPrefix !== undefined && slugPrefix.trim() !== '') {
      slug = `${slugPrefix}_${logActionSlug}`;
    }
    this.auth.apiCreate( this.routes.LOG,
      {
        slug: slug,
        data: {
          uid: this.auth.getUid(),
          ...body
        }
      }
    )
  }

  goToDashboardView = (view: StudentDashboardView, isOsslt?: boolean) => {
    if(isOsslt){
      const baseURL = `${this.lang.c()}/student/dashboard`;
      this.router.navigate([`${baseURL}/${view}`]);
    } else {
      const baseURL = `${this.lang.c()}/student/dashboard`;
      this.router.navigate([`${baseURL}/${view}`]);
    }
  }

  configureQueryParams(allow_inactive_subsession?: boolean) {
    if (this.auth.u()) {
      return {
        query: {
          schl_class_group_id: this.auth.u().sch_class_group_id,
          schl_class_id: this.auth.u().sch_class_id,
          allow_inactive_subsession: allow_inactive_subsession
        }
      }
    }
    return null;
  }

  async loadAssessments(allow_inactive_subsession?: boolean){
    return this.auth
      .apiFind(this.routes.STUDENT_SESSION, this.configureQueryParams(allow_inactive_subsession))
  }

  /**
   * Checks whether the Redis session cache is initialized for the given school class id
   * If not, shows a popup and throws an error to prevent the student from accessing the test
   * @throws ERR_SESSION_CACHE_NOT_INITIALIZED - if the session cache is not initialized
   */
  async checkRedisCacheInitialized() {
    const res = await this.auth.apiGet(this.routes.STUDENT_SESSION_CACHE_INIT_CHECK, this.auth.u().sch_class_id, {
      query: {
        schl_class_group_id: this.auth.u().sch_class_group_id
      }
    });

    if(!res?.isSessionInitialized) {
      this.loginGuard.quickPopup('txt_student_no_curr_asmt')
      throw new Error('ERR_SESSION_CACHE_NOT_INITIALIZED');
    }
  }

  isRelyingOnExtension = false;
  async loadAttempt(test_session_id, route:string = this.routes.STUDENT_SESSION, onExitLockCallback?: () => void){
    const sch_class_group_id = this.auth.u()?.sch_class_group_id;
    if(!sch_class_group_id) {
      return Promise.resolve();
    }

    // For OSSLT: use Verified Access as managed kiosk proof when Assessment Lock is present.
    let kioskPassword = this.auth.activeKioskPassword;
    let deviceTrustToken: string | null = null;
    this.isRelyingOnExtension = false;
    this.verifiedAccessDebug.markLoadAttemptPreparing(
      test_session_id,
      this.assessmentLock.currentState.extensionStatus,
      this.assessmentLock.isExtensionAvailable
    );
    if (this.assessmentLock.isExtensionAvailable || kioskPassword === 'EXTENSION_DETECTED'){
      this.isRelyingOnExtension = true;
      try {
        const deviceTrust = await this.assessmentLock.prepareDeviceTrust(test_session_id);
        deviceTrustToken = deviceTrust.device_trust_token;
      } catch (err) {
        console.error('Verified Access failed:', err);
        kioskPassword = 'EXTENSION_DETECTED';
        this.verifiedAccessDebug.markByodFallback(err);
      }
    }

    const query:any = {
      schl_class_group_id: sch_class_group_id
    };
    if (deviceTrustToken) {
      query.device_trust_token = deviceTrustToken;
    }
    if (kioskPassword && (kioskPassword !== 'EXTENSION_DETECTED' || !deviceTrustToken)) {
      query.KIOSK_PASSWORD = kioskPassword;
    }
    this.verifiedAccessDebug.markLoadAttemptPending(deviceTrustToken, query.KIOSK_PASSWORD);

    return this.auth
      .apiGet(route, test_session_id, {query}).then(async(res) => {
        this.verifiedAccessDebug.markLoadAttemptFetched();
        const attempt = res[0];
        this._enforceTdLang = attempt.enforce_assessment_lang;
        if(attempt.lang && this.lang.knownLangCodes.includes(attempt.lang) && attempt.lang !== this.lang.c() && attempt.enforce_assessment_lang){
          this.lang.setCurrentLanguage(attempt.lang);
        }
        this.assessmentLock.ensureChrombookAllow(attempt.allowNonChromebookExtension);
        const supervisorPassword = attempt.kiosk_password;
        this.assessmentLock.setSupervisorPassword(supervisorPassword);
        if (this.isRelyingOnExtension
          && (attempt.allowNonChromebookExtension || this.assessmentLock.isChromebook())
          && !attempt.assistiveTech
        ){
          if (!this.assessmentLock.isExtensionAvailable){
            this.blockAttempt('err_ldb_extenstion_unavail');
            throw new Error('ERR_EXTENSION_NOT_AVAILABLE');
          }
          if (!supervisorPassword){
            this.blockAttempt('err_ldb_extenstion_not_configured');
            throw new Error('ERR_EXTENSION_NOT_AVAILABLE');
          }
          this.assessmentLock.setAssessmentLanguage(this.lang.getCurrentLanguage());
          const exitCallback = onExitLockCallback || (() => this.goToDashboardView(StudentDashboardView.MAIN));

          // Compile extension allowed list, only applies to students with assistive tech accommodation.
          const studentHasAssistiveTechAccomm = attempt.assistiveTech;
          const extensionAllowlist = studentHasAssistiveTechAccomm ? attempt.assessmentLockPolicy?.extensionAllowlist : []
          
          this.assessmentLock.activateLock(exitCallback, {
            blockExtensions: attempt.assessmentLockPolicy?.isExtensionBlockingEnabled === true,
            extensionAllowlist: extensionAllowlist
          });
        }

        const isManagedKiosk = this.assessmentLock.currentDeviceTrustMode === 'managed_kiosk';
        const isSoftlockBypassed = !!attempt.isSoftlockBypassed || isManagedKiosk;

        this.schoolSoftLockEnable = !!attempt.is_softlock_enabled;
        this.schoolSoftLockPjEnable = !!attempt.is_softlock_enabled_pj;
        this.schoolSoftLockG9Enable = !!attempt.is_softlock_enabled_g9 && !isSoftlockBypassed;
        this.schoolSoftLockG10Enable = !!attempt.is_softlock_enabled_g10 && !isSoftlockBypassed;
        this.verifiedAccessDebug.markSoftlockStatus(
          false,
          isSoftlockBypassed ? 'disabled_managed_kiosk' : 'loaded_from_attempt',
          'dashboard',
          {
            testAttemptId: attempt.attemptId,
            softLockMode: attempt.softlock_mode,
            softLockRuntime: isSoftlockBypassed ? 'bypassed' : 'configured_not_running',
            softLockBypassReason: isSoftlockBypassed ? 'managed_kiosk' : null
          }
        );
        this.onlineOrPaper.setIsPaper(attempt.IsCrScanDefault, this.g9DemoData.asmtSlugToCurricShort(attempt.asmt_slug));
        if(attempt.styleProfileId){
          try {
            await this.styleprofile.setAttemptStyleProfile(attempt.styleProfileId);
          } catch(err) {
            this.blockAttempt('attempt_error_loading_test_design_style_profile');
          }
        } else {
          this.blockAttempt('attempt_error_test_design_missing_style_profile');
          this.styleprofile.checkStyleProfileLoaded({componentSource: 'STUDENT_DASHBOARD_SERVICE'}, true)
        }
        this.verifiedAccessDebug.markLoadAttemptSuccess();
        return res;
      })
      .catch(err => {
        if (err?.message === 'BYOD_BLOCKED_BY_SCHOOL') {
          this.verifiedAccessDebug.markLoadAttemptBlocked('BYOD_BLOCKED_BY_SCHOOL');
        } else {
          this.verifiedAccessDebug.markLoadAttemptFailed(err);
        }
        throw err;
      })
  }

  private _firstAttemptLangLoaded = false;
  async setfirstAttemptLang(){
    try {
      const langInfo = await this.auth
        .apiFind(this.routes.STUDENT_SESSION_LANG_INFO, this.configureQueryParams());
        if(langInfo){
            this._tdLang = langInfo?.lang
        }
    } catch{}
    this._firstAttemptLangLoaded = true
  }

  blockAttempt(message:string){
    this.loginGuard.confirmationReqActivate({
      caption: message,
      btnCancelConfig: {
        hide: true
      },
      btnProceedConfig:{
        caption: "btn_reload"
      },
      confirm: () => window.location.reload(),
      blockOthers: true
    })
  }

  getSchoolSoftLockEnablePj() {
    return this.schoolSoftLockPjEnable;
  }

  getSchoolSoftLockEnableG9() {
    return this.schoolSoftLockG9Enable;
  }

  getSchoolSoftLockEnableG10() {
    return this.schoolSoftLockG10Enable;
  }

  getSchoolSoftLockEnable(){
    return this.schoolSoftLockEnable
  }

  get enforceTdLang(){
    return this._enforceTdLang
  }

  get firstAttemptLangLoaded(){
    return this._firstAttemptLangLoaded
  }

  get tdLang(){
    return this._tdLang
  }
}
